import React, { useEffect, useState, useMemo } from "react";
import Header from "./Header";
import api from "../api/api";

export default function OrderRatingsFeedBack() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  // Filter counts
  const [filterCounts, setFilterCounts] = useState({
    All: 0,
    "5 Star": 0,
    "4 Star": 0,
    "3 Star": 0,
    "2 Star": 0,
    "1 Star": 0,
  });

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const res = await api.GetOrderRatingsFeedback("admin");
      // Fixed: API returns data directly, not wrapped in .data
      const data = res?.GetOrderRatingsFeedbackjs?.Table || [];
      setOrders(data);
      calculateFilterCounts(data);
    } catch (err) {
      console.error("Error fetching ratings:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate filter counts
  const calculateFilterCounts = (data) => {
    const counts = {
      All: data.length,
      "5 Star": data.filter((o) => o.ratings === 5).length,
      "4 Star": data.filter((o) => o.ratings === 4).length,
      "3 Star": data.filter((o) => o.ratings === 3).length,
      "2 Star": data.filter((o) => o.ratings === 2).length,
      "1 Star": data.filter((o) => o.ratings === 1).length,
    };
    setFilterCounts(counts);
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = orders.length;
    const totalRevenue = orders.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const avgRating = total > 0
      ? (orders.reduce((sum, item) => sum + Number(item.ratings || 0), 0) / total).toFixed(2)
      : "0.00";

    const breakdown = {
      5: orders.filter((x) => x.ratings === 5).length,
      4: orders.filter((x) => x.ratings === 4).length,
      3: orders.filter((x) => x.ratings === 3).length,
      2: orders.filter((x) => x.ratings === 2).length,
      1: orders.filter((x) => x.ratings === 1).length,
    };

    // Unique customers
    const uniqueCustomers = [...new Set(orders.map((o) => o.Vid))].length;

    // Actual feedback count (excluding N/A, No)
    const actualFeedback = orders.filter(
      (x) => x.feedback && 
      x.feedback.toLowerCase() !== "n/a" && 
      x.feedback.toLowerCase() !== "no" &&
      x.feedback.trim() !== ""
    ).length;

    return { total, totalRevenue, avgRating, breakdown, uniqueCustomers, actualFeedback };
  }, [orders]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Apply rating filter
    if (activeFilter !== "All") {
      const ratingValue = parseInt(activeFilter.charAt(0));
      result = result.filter((o) => o.ratings === ratingValue);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((order) =>
        Object.values(order).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        })
      );
    }

    return result;
  }, [orders, activeFilter, searchQuery]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    window.location.href = "/";
  };

  // Handle back navigation
  const handleBack = () => window.history.back();

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Order ID", "Customer Name", "Mobile", "Order Date", "Amount", "Rating", "Feedback"];
    const rows = filteredOrders.map((item) => [
      item.oid,
      item.vname?.trim(),
      item.vcode,
      new Date(item.orderdate).toLocaleDateString("en-GB"),
      item.amount,
      item.ratings,
      item.feedback || "N/A"
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ratings_feedback_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Render stars
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={`text-lg ${i <= rating ? "text-orange-400" : "text-gray-300"}`}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  // Get rating badge color
  const getRatingBadgeClass = (rating) => {
    if (rating >= 4) return "bg-green-100 text-green-700";
    if (rating >= 3) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="p-4 mx-auto mt-3 bg-white shadow-2xl sm:p-6 rounded-2xl max-w-8xl">
          
          {/* Header Section */}
          <div className="flex flex-col justify-between gap-3 pb-3 mb-4 border-b sm:flex-row sm:items-center">
            <button
              onClick={handleBack}
              className="inline-flex items-center self-start w-auto gap-1 px-3 py-1 text-sm font-semibold text-gray-800 transition bg-gray-200 rounded-full shadow hover:bg-gray-300 sm:text-base"
            >
              ← Back
            </button>

            <h1 className="flex-1 text-2xl font-bold text-center text-orange-600 sm:text-3xl">
              ⭐ Ratings & Feedback
            </h1>

            <button
              onClick={exportToCSV}
              className="inline-flex self-start w-auto px-2 py-1 font-semibold text-white transition bg-blue-500 rounded-full shadow hover:bg-blue-700"
            >
              📄 Export CSV
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="p-4 text-center bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
              <p className="text-xs text-gray-500">Average Rating</p>
              <p className="text-2xl font-bold text-orange-600">{stats.avgRating}</p>
              <div className="flex justify-center mt-1">
                {renderStars(Math.round(parseFloat(stats.avgRating)))}
              </div>
            </div>
            <div className="p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <p className="text-xs text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
            <div className="p-4 text-center bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <p className="text-xs text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">₹{stats.totalRevenue.toLocaleString()}</p>
            </div>
            <div className="p-4 text-center bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <p className="text-xs text-gray-500">Customers</p>
              <p className="text-2xl font-bold text-purple-600">{stats.uniqueCustomers}</p>
            </div>
            <div className="p-4 text-center bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl border border-pink-200">
              <p className="text-xs text-gray-500">Feedbacks</p>
              <p className="text-2xl font-bold text-pink-600">{stats.actualFeedback}</p>
            </div>
          </div>

          {/* Rating Breakdown */}
          <div className="p-4 mb-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl">
            <h3 className="mb-3 text-sm font-semibold text-gray-700">📊 Rating Breakdown</h3>
            <div className="grid grid-cols-5 gap-2">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="text-orange-500">{star}</span>
                    <span className="text-orange-400">★</span>
                  </div>
                  <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        star >= 4 ? "bg-green-400" : star >= 3 ? "bg-yellow-400" : "bg-red-400"
                      }`}
                      style={{
                        width: `${stats.total ? (stats.breakdown[star] / stats.total) * 100 : 0}%`,
                      }}
                    ></div>
                  </div>
                  <p className="mt-1 text-xs font-semibold text-gray-600">{stats.breakdown[star]}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {["All", "5 Star", "4 Star", "3 Star", "2 Star", "1 Star"].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full font-medium transition shadow-sm ${
                  activeFilter === filter
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {filter}
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeFilter === filter
                      ? "bg-white text-orange-600"
                      : "bg-orange-100 text-orange-600"
                  }`}
                >
                  {filterCounts[filter]}
                </span>
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="🔍 Search by name, mobile, feedback..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-4 border-t-4 border-orange-500 rounded-full animate-spin"></div>
                <p className="text-gray-600">Loading ratings data...</p>
              </div>
            </div>
          ) : (
            /* Table */
            <div className="overflow-x-auto border border-gray-200 shadow-inner rounded-xl">
              <table className="min-w-full text-sm text-gray-700">
                <thead className="sticky top-0 text-white bg-gradient-to-r from-orange-500 to-orange-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Sl No</th>
                    <th className="px-3 py-2 text-left">Order ID</th>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Mobile</th>
                    <th className="px-3 py-2 text-left">Order Date</th>
                    <th className="px-3 py-2 text-left">Amount</th>
                    <th className="px-3 py-2 text-left">Rating</th>
                    <th className="px-3 py-2 text-left">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length > 0 ? (
                    filteredOrders.map((order, index) => (
                      <tr
                        key={order.oid}
                        className={`border-b hover:bg-orange-50 transition-colors ${
                          order.ratings <= 2 ? "bg-red-50" : index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-2 font-medium">{index + 1}</td>
                        <td className="px-3 py-2 font-semibold">#{order.oid}</td>
                        <td className="px-3 py-2">{order.vname?.trim()}</td>
                        <td className="px-3 py-2">{order.vcode}</td>
                        <td className="px-3 py-2">{formatDate(order.orderdate)}</td>
                        <td className="px-3 py-2 font-semibold text-green-600">
                          ₹{Number(order.amount).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRatingBadgeClass(order.ratings)}`}>
                            {order.ratings} ★
                          </span>
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate">
                          {order.feedback && order.feedback.toLowerCase() !== "n/a" && order.feedback.toLowerCase() !== "no" ? (
                            <span className={order.ratings <= 2 ? "text-red-600 font-medium" : "text-gray-600"}>
                              {order.feedback}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-3 py-8 text-center text-gray-500">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Results Count */}
          {!loading && (
            <div className="mt-3 text-sm text-center text-gray-500">
              Showing {filteredOrders.length} of {orders.length} orders
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
